import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import isEmpty from "lodash/isEmpty";
import { orderBy } from "lodash";
import {
  getSamplesForEachDatabase,
  getDataFromAllDatabasesForSpecificKey,
  getChartData,
} from "./helpers";
import { osStatusKeys, initialDbsState } from "./constants";
import Charts from "./Charts";
import { Checkbox } from "../../../../components/atoms";
import { getCancerHotspotsData } from "../../../../state/selectors/CancerHotspotsComponent.selectors";
import { getVariantId } from "../../../../state/selectors/general.selectors";
import { getCbioPortalData } from "../../../../state/selectors/CBioPortalComponent.selectors";
import { getGDCData } from "../../../../state/selectors/GDCComponent.selectors";
import {
  getTp53Germline,
  getTp53Somatic,
} from "../../../../state/selectors/Tp53.selectors";
import { isThaliaVersionSmallerThanNinePointOne } from "../../../../state/selectors/ACMGComponent.selectors";
import { formatNumber } from "../../../../common/helpers/helpers";
import withErrorGuard from "../../../../HOC/withErrorGuard";
import ComponentPanel from "../../../../components/atoms/ComponentPanel/ComponentPanel";

const CancerChartsAggregator = (props) => {
  const {
    variantId,
    isVersionSmallerThanNinePointOne,
    icgcSomaticData,
    iarctp53SomaticData,
    iarctp53GermlineData,
    cancerHotspotsData,
    gdcData,
    cbioPortalData,
  } = props;

  const [databases, setDatabases] = useState(initialDbsState);

  const setDatabaseState = (data, label) => {
    const db = databases.find((obj) => obj.label === label);
    const samples =
      label === "ICGC Somatic"
        ? data?.affected_donors || 0
        : data?.total_samples || 0;

    return {
      ...db,
      hasData: !!data && !isEmpty(data),
      data,
      samples,
    };
  };

  const updateDatabasesState = () => {
    setDatabases([
      setDatabaseState(icgcSomaticData, "ICGC Somatic"),
      setDatabaseState(iarctp53SomaticData, "ΙΑRCTP53 Somatic"),
      setDatabaseState(iarctp53GermlineData, "ΙΑRCTP53 Germline"),
      setDatabaseState(cancerHotspotsData, "Cancer Hotspots"),
      setDatabaseState(gdcData, "GDC"),
      setDatabaseState(cbioPortalData, "cBioPortal"),
    ]);
  };

  useEffect(() => {
    updateDatabasesState();
  }, [variantId]);

  const checkboxHandler = (label) => {
    setDatabases((prevDatabases) => {
      const updatedDatabases = prevDatabases.map((db) =>
        db.label === label ? { ...db, isChecked: !db.isChecked } : db
      );
      return updatedDatabases;
    });
  };

  const sexData = getChartData(databases, "sex", false);
  const tumorData = getChartData(databases, "tumor_status", false);
  const cancerTypeData = orderBy(
    getChartData(databases, "cancer_type", false),
    "value",
    "desc"
  );
  const cancerNameData = orderBy(
    getChartData(databases, "cancer_name", false),
    "value",
    "desc"
  );
  const vitalData = getChartData(databases, "os_status", true, osStatusKeys);
  const samplesData = getSamplesForEachDatabase(databases);
  const summaryTableData = [...databases].sort((a, b) => b.samples - a.samples);
  const ageData = getDataFromAllDatabasesForSpecificKey(databases, "age_freq");

  if (isVersionSmallerThanNinePointOne && __IS_COMPONENTS_PORTAL__) return null;

  const displaySumaryTableAndCharts =
    !isEmpty(samplesData) ||
    !isEmpty(sexData) ||
    !isEmpty(vitalData) ||
    !isEmpty(ageData) ||
    !isEmpty(cancerTypeData) ||
    !isEmpty(cancerNameData) ||
    !isEmpty(tumorData);

  const componentContent = (
    <div className="tw-p-5">
      <div className="tw-inline-flex tw-flex-col">
        <div className="legend">
          <div className="legend__header">
            <h4 className="title">
              Data Summary
              <span className="desc">
                see individual databases for more data
              </span>
            </h4>
          </div>
          <div className="legend__body">
            <div
              className="varsome_table"
              data-testid="cancer-charts-aggregator-table"
            >
              <table>
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {displaySumaryTableAndCharts
                    ? summaryTableData.map(
                        (database) =>
                          database.hasData && (
                            <tr
                              key={database.label}
                              data-testid={`${database.label}-label`}
                            >
                              <td>
                                <Checkbox
                                  key={database.label}
                                  isChecked={database.isChecked}
                                  label={database.label}
                                  onCheckboxClick={() =>
                                    checkboxHandler(database.label)
                                  }
                                  styles={{
                                    padding: 0,
                                    margin: 0,
                                  }}
                                />
                              </td>
                              <td
                                className="numeric"
                                data-testid={`${database.label}-samples`}
                              >
                                {formatNumber(database.samples)}
                              </td>
                            </tr>
                          )
                      )
                    : summaryTableData.map((database) => (
                        <tr key={database.label}>
                          <td>{database.label}</td>
                          <td className="numeric">
                            {formatNumber(database.samples)}
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {displaySumaryTableAndCharts && (
        <Charts
          samplesData={samplesData}
          sexData={sexData}
          vitalData={vitalData}
          ageData={ageData}
          cancerTypeData={cancerTypeData}
          cancerNameData={cancerNameData}
          tumorData={tumorData}
        />
      )}
    </div>
  );
  return (
    <div className="varsome-result-fixed">
      <div className="varsome-container varsome-container__basic">
        <ComponentPanel
          content={componentContent}
          title="Aggregate across all sources"
        />
      </div>
    </div>
  );
};

CancerChartsAggregator.propTypes = {
  variantId: PropTypes.string,
  icgcSomaticData: PropTypes.shape({}),
  iarctp53SomaticData: PropTypes.shape({}),
  iarctp53GermlineData: PropTypes.shape({}),
  cancerHotspotsData: PropTypes.shape({}),
  gdcData: PropTypes.shape({}),
  cbioPortalData: PropTypes.shape({}),
  isVersionSmallerThanNinePointOne: PropTypes.bool.isRequired,
};

CancerChartsAggregator.defaultProps = {
  variantId: null,
  icgcSomaticData: {},
  iarctp53SomaticData: {},
  iarctp53GermlineData: {},
  cancerHotspotsData: {},
  gdcData: {},
  cbioPortalData: {},
};

const mapStateToProps = (state) => {
  return {
    variantId: getVariantId(state),
    icgcSomaticData:
      state.variantResult &&
      state.variantResult.icgc_somatic &&
      state.variantResult.icgc_somatic[0],
    iarctp53SomaticData: getTp53Somatic(state),
    iarctp53GermlineData: getTp53Germline(state),
    cancerHotspotsData: getCancerHotspotsData(state),
    gdcData: getGDCData(state),
    cbioPortalData: getCbioPortalData(state),
    isVersionSmallerThanNinePointOne:
      isThaliaVersionSmallerThanNinePointOne(state),
  };
};

export default connect(
  mapStateToProps,
  {}
)(
  withErrorGuard(
    CancerChartsAggregator,
    "CancerChartsAggregatorComponent",
    "Aggregate across all sources"
  )
);
